import axios from 'axios';
import { store } from '../store';
import router from '../routing/router';
import requestManager from '../composables/useRequestManager';

let apiClient;
export const createApiClient = () => 
{
  if(process.env.VUE_APP_API_ENV === 'laravel') axios.get(`//${process.env.VUE_APP_LUMEN_URL}/sanctum/csrf-cookie`, {withCredentials: true});
  apiClient = axios.create({ 
    withCredentials: process.env.VUE_APP_API_ENV === 'laravel',
    baseURL: `//${process.env.VUE_APP_LUMEN_URL}/v1`,
    headers: {
      'Content-Type': 'application/json',
      'Accept' : 'application/json'
    }
  });
  
  // Request Interceptor
  apiClient.interceptors.request.use((config) => 
  {
    const controller = requestManager.createController();
    config.signal = controller.signal;
    
    if(process.env.VUE_APP_API_ENV === 'lumen' && store.state.auth.user && store.state.auth.token && !config.headers.common.Authorization) 
    {
      config.headers = Object.assign(Object.assign({}, config.headers), {
        Accept: `application/json`,
        Language: store.state.locale,
        'Accept-Language': `it`,
        'Content-Type': `application/json`,
        Authorization: `Bearer ${store.state.auth.token}`
      })
    }
    else 
    {
      config.headers = Object.assign(Object.assign({}, config.headers), {
        Accept: `application/json`,
        Language: store.state.locale,
        'Accept-Language': `it`,
        'Content-Type': `application/json`
      })
    }

    return config
  });
  // Response Interceptor
  apiClient.interceptors.response.use(
    (response) => {
      return response;
    },
    function (e)
    {
      if(e.response)
      {
        // ZDHC domain errors (zdhc_gateway_disabled, zdhc_supplier_not_linked,
        // zdhc_conflict, …) are flow states handled by the calling service:
        // they must never trigger the global error-page redirects below.
        if(e.response.data && String(e.response.data.error || '').startsWith('zdhc_'))
        {
          throw e;
        }

        // Un 409 dice "la tua richiesta contrasta con lo stato attuale": lo
        // gestisce chi ha chiamato, non e' un guasto di pagina. Senza questo
        // ramo cadeva nel default dello switch qui sotto, dove `path` e'
        // ancora `/`, e portava l'utente via dalla pagina — con l'azione
        // rifiutata e nessuna spiegazione.
        if(e.response.status === 409)
        {
          throw e;
        }

        if( e.response &&[401, 419].includes(e.response.status) )
        {
          store.commit("auth/CLEAR_STATE");
        }
        else if((e.response && e.response.status === 422) || e.status === 422)
        {
          if(e.response.data )
          {
            store.commit('errors/SET_ERRORS', e.response.data);
          }
          return 422;
        }

        // Chiamate dichiaratamente best-effort: chi le fa gestisce l'errore da
        // se' e il fallimento non deve portare via l'utente dalla pagina che
        // sta guardando. Serve soprattutto quando il client arriva prima
        // dell'api: una rotta che ancora non esiste risponde 404, e un 404 qui
        // sotto diventa una redirezione alla pagina "non trovato".
        if(e.config && e.config.skipErrorRedirect) throw e;

        let path = '/';
          switch(e.response.status){
            case 419,401: path = "signin"; break;
            case 403: path = "forbidden"; break;
            case 404: path = "notFound"; break;
            case 500: path = "InternalServerError"; break;
            case 503: path = "ServiceUnavailable"; break;
          }
        router.push(`/${store.state.locale}/${path}`);
      }
      throw e;
    } 
  );
};

/**
 * Se il guasto e' della rete e non del nostro codice.
 *
 * Serve a chi vuole degradare un errore di trasporto senza inghiottire anche i
 * propri bug: un `TypeError` dentro un servizio deve risalire e farsi vedere,
 * una connessione caduta no. La conoscenza di come axios marca le sue eccezioni
 * sta qui, dove axios e' importato, invece di sparpagliarsi nelle viste.
 *
 * `isAxiosError` **non basta**: su questa versione lo portano anche le risposte
 * 4xx e 5xx, che sono risposte e non guasti di trasporto. Oggi non arrivano fin
 * qui perche' `call()` intercetta prima tutto cio' che ha una `response`, ma un
 * predicato che si chiama "trasporto" non deve dipendere da quella cortesia:
 * l'assenza di `response` e' cio' che distingue davvero le due cose.
 *
 * Le cancellazioni si guardano a parte perche' non sono `AxiosError` su questa
 * versione, e ogni richiesta passa dal `requestManager`, che le annulla.
 */
export const isTransportError = (error) =>
    axios.isCancel(error) || (axios.isAxiosError(error) && ! error.response);

export const useApi = () => 
{
  if(!apiClient) createApiClient();
  return apiClient
};